import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IMLPolicyResponse, IMLRcResponse, IProposal } from 'Models/ResponseModels'
import { TMotorProposalFormType } from 'Pages/Motor/Proposal/Forms/FormModel'
import { ISA, TAddressPriority } from 'Pages/Motor/Proposal/Forms/ProposalCustomerDetailsForm'
import { ToastService } from 'Services/toast.service'

type TCbsFields =
  | 'customerFirstName'
  | 'customerMiddleName'
  | 'customerLastName'
  | 'customerEmail'
  | 'customerMobile'
  | 'customerDob'
  | 'customerAddress1'
  | 'customerAddress2'
  | 'customerPincode'
  | 'customerPermanentPincode'

export type inputTypes =
  | 'firstName'
  | 'lastName'
  | 'middleName'
  | 'email'
  | 'dob'
  | 'address1'
  | 'address2'
  | 'mobileNumber'

export interface IProposalPageState {
  proposalStatus: 'success' | 'loading' | 'error' | 'not-fetched'
  proposalScreen: 'one' | 'two' | 'three' | 'four' | 'five'
  quotation_details: any
  proposalDetails: TMotorProposalFormType
  proposalSuccessResponse: IProposal
  proposalErrorResponse: string
  consentFormLink: string
  paymentActivated: boolean
  paymentSuccess: boolean
  isNomineeMandatory: boolean
  isNomineeSelected: boolean
  selectedAddressPriority: {
    id: string
    address1: string
    address2: string
    type: TAddressPriority
  }
  readOnlyInputs: inputTypes[]
  fieldsFetchedFromCbsMapping: {
    customerFirstName: boolean
    customerMiddleName: boolean
    customerLastName: boolean
    customerEmail: boolean
    customerMobile: boolean
    customerDob: boolean
    customerAddress1: boolean
    customerAddress2: boolean
    customerPincode: boolean
    customerPermanentPincode: boolean
  }
  vahanAddress: {
    address1: string
    address2: string
    pincode: string
  }
  ckycAddress: {
    address1: string
    address2: string
    pincode: string
  }
  selectedPincode: string
  selectedPaymentMethod: 'DD' | 'PG'
}

const initialState: IProposalPageState = {
  proposalStatus: 'not-fetched',
  proposalScreen: 'one',
  quotation_details: '',
  isNomineeMandatory: false,
  isNomineeSelected: false,
  proposalDetails: {
    isLoan: false,
    salutation: 'Mr.',
    isGenderMale: true,
    isMarried: false,
  },
  proposalSuccessResponse: {},
  selectedAddressPriority: {
    id: '',
    address1: '',
    address2: '',
    type: '',
  },
  proposalErrorResponse: '',
  consentFormLink: '',
  paymentActivated: false,
  paymentSuccess: false,
  readOnlyInputs: [],
  fieldsFetchedFromCbsMapping: {
    customerFirstName: false,
    customerMiddleName: false,
    customerLastName: false,
    customerEmail: false,
    customerMobile: false,
    customerDob: false,
    customerAddress1: false,
    customerAddress2: false,
    customerPincode: false,
    customerPermanentPincode: false,
  },
  vahanAddress: {
    address1: '',
    address2: '',
    pincode: '',
  },
  ckycAddress: {
    address1: '',
    address2: '',
    pincode: '',
  },
  selectedPincode: '',
  selectedPaymentMethod: 'DD',
}

const proposalPageSlice = createSlice({
  name: 'proposalPage',
  initialState: initialState,
  reducers: {
    updateSelectedPaymentMethod: (state, action: PayloadAction<'DD' | 'PG'>) => {
      state.selectedPaymentMethod = action.payload
    },
    updateVahanAddress: (state, action) => {
      state.vahanAddress.address1 = action.payload.address1 ?? ''
      state.vahanAddress.address2 = action.payload.address2 ?? ''
      state.vahanAddress.pincode = action.payload.pincode ?? ''
    },
    updateCKYCAddress: (state, action) => {
      state.ckycAddress.address1 = action.payload.address1 ?? ''
      state.ckycAddress.address2 = action.payload.address2 ?? ''
      state.ckycAddress.pincode = action.payload.pincode ?? ''
    },
    updateIsNomineeMandatory: (state, action: PayloadAction<boolean>) => {
      state.isNomineeMandatory = action.payload
    },
    updateIsNomineeSelected: (state, action: PayloadAction<boolean>) => {
      state.isNomineeSelected = action.payload
    },
    updateReadOnlyInputs: (state, action: PayloadAction<inputTypes[]>) => {
      state.readOnlyInputs = action.payload
    },
    toggleProposalScreen: (
      state,
      action: PayloadAction<'one' | 'two' | 'three' | 'four' | 'five'>,
    ) => {
      state.proposalScreen = action.payload
    },
    updateProposalDetails: (state, action: PayloadAction<TMotorProposalFormType>) => {
      const {
        engineNumber,
        chassisNumber,
        previousPolicyNumber,
        isLoan,
        bankName,
        hypothecation,
        usesType,
        salutation,
        firstName,
        middleName,
        lastName,
        companyName,
        gstNumber,
        isGenderMale,
        isMarried,
        panCard,
        mobileNumber,
        email,
        pincodeArea,
        occupation,
        address1,
        address2,
        dob,
        landmark,
        nomineeFirstName,
        nomineeMiddleName,
        nomineeLastName,
        nomineeDob,
        nomineeAge,
        appointeeFirstName,
        appointeeMiddleName,
        appointeeLastName,
        appointeeDob,
        appointeeRelationship,
        otherRelationship,
        agreementType,
        nomineeRelationship,
        pucExpiryDate,
        pucNumber,
        // selectedPincode,
      } = action.payload
      if (otherRelationship) state.proposalDetails.otherRelationship = otherRelationship
      if (pucExpiryDate) state.proposalDetails.pucExpiryDate = pucExpiryDate
      if (pucNumber) state.proposalDetails.pucNumber = pucNumber
      if (engineNumber) state.proposalDetails.engineNumber = engineNumber
      if (chassisNumber) state.proposalDetails.chassisNumber = chassisNumber
      if (previousPolicyNumber) state.proposalDetails.previousPolicyNumber = previousPolicyNumber
      // if (isLoan) state.proposalDetails.isLoan = isLoan
      if (agreementType) state.proposalDetails.agreementType = agreementType
      if (isLoan === true || isLoan === false) {
        state.proposalDetails.isLoan = isLoan
      }
      if (bankName) state.proposalDetails.bankName = bankName

      if (hypothecation) state.proposalDetails.hypothecation = hypothecation

      if (usesType) {
        state.proposalDetails.usesType = usesType
      }

      if (salutation) state.proposalDetails.salutation = salutation
      if (firstName) state.proposalDetails.firstName = firstName
      if (middleName) state.proposalDetails.middleName = middleName
      if (lastName) state.proposalDetails.lastName = lastName

      if (companyName) state.proposalDetails.companyName = companyName

      if (gstNumber) state.proposalDetails.gstNumber = gstNumber

      // if (isGenderMale) state.proposalDetails.isGenderMale = isGenderMale
      if (isGenderMale === true || isGenderMale === false) {
        state.proposalDetails.isGenderMale = isGenderMale
      }
      // if (isMarried) state.proposalDetails.isMarried = isMarried
      if (isMarried === true || isMarried === false) {
        state.proposalDetails.isMarried = isMarried
      }
      if (panCard) state.proposalDetails.panCard = panCard
      if (mobileNumber) state.proposalDetails.mobileNumber = mobileNumber
      if (email) state.proposalDetails.email = email
      if (pincodeArea) state.proposalDetails.pincodeArea = pincodeArea
      if (occupation) state.proposalDetails.occupation = occupation
      if (address1) state.proposalDetails.address1 = address1
      if (address2) state.proposalDetails.address2 = address2
      if (dob) state.proposalDetails.dob = dob
      if (landmark) state.proposalDetails.landmark = landmark
      if (nomineeFirstName) state.proposalDetails.nomineeFirstName = nomineeFirstName
      if (nomineeMiddleName) state.proposalDetails.nomineeMiddleName = nomineeMiddleName
      if (nomineeLastName) state.proposalDetails.nomineeLastName = nomineeLastName
      if (nomineeDob) state.proposalDetails.nomineeDob = nomineeDob
      if (nomineeAge) state.proposalDetails.nomineeAge = nomineeAge
      if (appointeeFirstName) {
        state.proposalDetails.appointeeFirstName = appointeeFirstName
      }
      if (appointeeMiddleName) {
        state.proposalDetails.appointeeMiddleName = appointeeMiddleName
      }
      if (appointeeLastName) {
        state.proposalDetails.appointeeLastName = appointeeLastName
      }
      if (appointeeRelationship) {
        state.proposalDetails.appointeeRelationship = appointeeRelationship
      }
      if (appointeeDob) {
        state.proposalDetails.appointeeDob = appointeeDob
      }
      if (nomineeRelationship) state.proposalDetails.nomineeRelationship = nomineeRelationship
    },
    resetNomineeDetails: (state) => {
      state.proposalDetails.nomineeFirstName = initialState.proposalDetails.nomineeFirstName
      state.proposalDetails.nomineeMiddleName = initialState.proposalDetails.nomineeMiddleName
      state.proposalDetails.nomineeLastName = initialState.proposalDetails.nomineeLastName
      state.proposalDetails.nomineeAge = initialState.proposalDetails.nomineeAge
      state.proposalDetails.nomineeDob = initialState.proposalDetails.nomineeDob
      state.proposalDetails.nomineeRelationship = initialState.proposalDetails.nomineeRelationship
    },
    resetAppointeeDetails: (state) => {
      state.proposalDetails.appointeeFirstName = initialState.proposalDetails.appointeeFirstName
      state.proposalDetails.appointeeMiddleName = initialState.proposalDetails.appointeeMiddleName
      state.proposalDetails.appointeeLastName = initialState.proposalDetails.appointeeLastName
      state.proposalDetails.appointeeDob = initialState.proposalDetails.appointeeDob
      state.proposalDetails.appointeeRelationship =
        initialState.proposalDetails.appointeeRelationship
    },
    updateProposalSuccessResponse: (state, action: PayloadAction<IProposal>) => {
      state.proposalSuccessResponse = action.payload
    },
    updateProposalErrorResponse: (state, action: PayloadAction<string>) => {
      state.proposalErrorResponse = action.payload
    },
    updateSelectedAddressPriority: (state, action: PayloadAction<ISA>) => {
      state.selectedAddressPriority = action.payload
    },
    updateSelectedPincode: (state, action: PayloadAction<string>) => {
      state.selectedPincode = action.payload
    },
    resetSelectedAddressPriority: (state) => {
      state.selectedAddressPriority = initialState.selectedAddressPriority
    },
    resetSelectedPincode: (state) => {
      state.selectedPincode = initialState.selectedPincode
    },
    resetIsNomineeMandatory: (state) => {
      state.isNomineeMandatory = initialState.isNomineeMandatory
    },
    resetIsNomineeSelected: (state) => {
      state.isNomineeSelected = initialState.isNomineeSelected
    },
    updateProposalButtonLoadingStatus: (
      state,
      action: PayloadAction<'success' | 'loading' | 'error' | 'not-fetched'>,
    ) => {
      state.proposalStatus = action.payload
    },
    updateFieldsFetchedFromCbsMapping: (state, action: PayloadAction<TCbsFields>) => {
      state.fieldsFetchedFromCbsMapping[action.payload] = true
    },
    updatePolicyDataFromOCR: (state, action: PayloadAction<IMLPolicyResponse>) => {
      const {
        engine_number,
        chassis_number,
        first_name,
        middle_name,
        last_name,
        address1,
        address2,
        prev_policy_number,
        landmark,
        email,
        phone_number,
      } = action.payload
      state.proposalDetails.engineNumber = engine_number
      state.proposalDetails.chassisNumber = chassis_number
      state.proposalDetails.firstName = first_name
      state.proposalDetails.middleName = middle_name
      state.proposalDetails.lastName = last_name
      state.proposalDetails.address1 = address1
      state.proposalDetails.address2 = address2

      state.proposalDetails.previousPolicyNumber = prev_policy_number
      state.proposalDetails.landmark = landmark
      state.proposalDetails.email = email
      state.proposalDetails.mobileNumber = phone_number

      ToastService.showSuccessTaost({
        title: 'Details are fetched from the RC copy uploaded',
        message: 'Please check the details before proceeding further',
      })
    },

    updatePolicyDataFromRC: (state, action: PayloadAction<IMLRcResponse>) => {
      const { address, engine_number, chassis_number, name } = action.payload
      state.proposalDetails.address1 = address?.substring(0, 50)
      state.proposalDetails.engineNumber = engine_number
      state.proposalDetails.chassisNumber = chassis_number
      state.proposalDetails.firstName = name

      ToastService.showSuccessTaost({
        title: 'Details are fetched from the RC copy uploaded',
        message: 'Please check the details before proceeding further',
      })
    },
    resetProposalStatus: (state) => {
      state.proposalStatus = 'not-fetched'
      // state.proposalSuccessResponse = {}
      state.proposalErrorResponse = ''
      state.proposalScreen = 'one'
      state.proposalDetails.pincodeArea = undefined
      state.consentFormLink = ''
      state.paymentActivated = false
      state.paymentSuccess = false
    },
    updateConsentFormLink: (state, action: PayloadAction<string>) => {
      state.consentFormLink = action.payload
    },
    updatePaymentActivated: (state, action: PayloadAction<boolean>) => {
      state.paymentActivated = action.payload
    },
    updatePaymentSuccess: (state, action: PayloadAction<boolean>) => {
      state.paymentSuccess = action.payload
    },
    resetProposal: () => initialState,
  },
})

const { reducer } = proposalPageSlice

const {
  toggleProposalScreen,
  updateSelectedPaymentMethod,
  updateProposalSuccessResponse,
  updateProposalErrorResponse,
  updateProposalDetails,
  resetAppointeeDetails,
  resetNomineeDetails,
  updateProposalButtonLoadingStatus,
  updatePolicyDataFromOCR,
  resetSelectedAddressPriority,
  updatePolicyDataFromRC,
  resetProposal,
  resetProposalStatus,
  updateConsentFormLink,
  updatePaymentActivated,
  updatePaymentSuccess,
  updateFieldsFetchedFromCbsMapping,
  updateReadOnlyInputs,
  updateVahanAddress,
  updateCKYCAddress,
  updateSelectedAddressPriority,
  updateIsNomineeMandatory,
  resetIsNomineeMandatory,
  updateSelectedPincode,
  resetSelectedPincode,
  updateIsNomineeSelected,
  resetIsNomineeSelected,
} = proposalPageSlice.actions

export {
  reducer as proposalPageReducer,
  toggleProposalScreen,
  updateSelectedPaymentMethod,
  updateProposalSuccessResponse,
  updateProposalErrorResponse,
  updateProposalDetails,
  resetSelectedAddressPriority,
  updateProposalButtonLoadingStatus,
  updateSelectedAddressPriority,
  updatePolicyDataFromOCR,
  updatePolicyDataFromRC,
  resetProposal,
  resetProposalStatus,
  updateConsentFormLink,
  updatePaymentActivated,
  updatePaymentSuccess,
  updateFieldsFetchedFromCbsMapping,
  updateReadOnlyInputs,
  updateVahanAddress,
  updateCKYCAddress,
  updateIsNomineeMandatory,
  resetIsNomineeMandatory,
  updateSelectedPincode,
  resetSelectedPincode,
  resetAppointeeDetails,
  resetNomineeDetails,
  updateIsNomineeSelected,
  resetIsNomineeSelected,
}
